/**
 * Custom error types for transaction balance management operations.
 * These errors provide detailed context for validation failures and operation errors.
 */

/**
 * Error thrown when a transaction would result in a negative account balance.
 * Provides detailed information about the balance shortfall.
 */
export class InsufficientBalanceError extends Error {
  public readonly currentBalance: number;
  public readonly requiredAmount: number;
  public readonly shortfall: number;
  public readonly currency: string;

  constructor(
    currentBalance: number,
    requiredAmount: number,
    shortfall: number,
    currency: string
  ) {
    super(
      `Insufficient balance: this transaction would result in a negative account balance. ` +
      `Current: ${currentBalance} ${currency}, Required: ${requiredAmount} ${currency}, ` +
      `Shortfall: ${shortfall} ${currency}`
    );
    this.name = 'InsufficientBalanceError';
    this.currentBalance = currentBalance;
    this.requiredAmount = requiredAmount;
    this.shortfall = shortfall;
    this.currency = currency;

    // Maintains proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, InsufficientBalanceError);
    }
  }
}

/**
 * Error thrown when a transaction cannot be found in the database.
 */
export class TransactionNotFoundError extends Error {
  public readonly transactionId: string;

  constructor(transactionId: string) {
    super(`Transaction not found: ${transactionId}`);
    this.name = 'TransactionNotFoundError';
    this.transactionId = transactionId;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TransactionNotFoundError);
    }
  }
}

/**
 * Error thrown when an account cannot be found in the database.
 */
export class AccountNotFoundError extends Error {
  public readonly accountId: string;

  constructor(accountId: string) {
    super(`Account not found: ${accountId}`);
    this.name = 'AccountNotFoundError';
    this.accountId = accountId;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AccountNotFoundError);
    }
  }
}

/**
 * Error thrown when an atomic operation (transaction + balance update) fails.
 * Wraps the underlying error cause for debugging.
 */
export class AtomicOperationError extends Error {
  public readonly operation: string;
  public readonly cause: Error;

  constructor(operation: string, cause: Error) {
    super(`Atomic operation failed: ${operation}. Cause: ${cause.message}`);
    this.name = 'AtomicOperationError';
    this.operation = operation;
    this.cause = cause;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AtomicOperationError);
    }
  }
}
